import { ProjectApiResponse, ProjectFormData, SelectedSkillStandard } from '../types'
import { transformApiProjectItemsToFormState } from './projectItems.transformers'

/**
 * Validates if a string is a valid base64 image
 * @param value - String to validate
 * @returns True if the string is a valid base64 image data URL
 */
export const isBase64 = (value: string): boolean => {
  const base64Regex = /^data:image\/([a-zA-Z]+);base64,/
  return base64Regex.test(value)
}

/** Payload structure for project creation (includes grades and kind) */
export interface ProjectPayload {
  name: string
  cover_image?: string | null
  kind: string
  purpose: string
  modality: string
  objectives: string
  stages: string
  final_product_kind: string
  final_product_description: string
  grades: string[]
  share_community: boolean
  synopsis: string
  evaluation_tools_attributes: unknown[]
}

/** Alias for ProjectPayload used in create operations */
export type ProjectCreatePayload = ProjectPayload

/** Payload structure for project updates (excludes grades and kind to avoid curriculum validation) */
export interface ProjectUpdatePayload {
  name: string
  cover_image?: string | null
  purpose: string
  modality: string
  objectives: string
  stages: string
  final_product_kind: string
  final_product_description: string
  share_community: boolean
  synopsis: string
  evaluation_tools_attributes: unknown[]
}

/**
 * Transforms ArcNewProject form data to API payload format for project creation
 *
 * @param formData - Form data from ArcNewProject
 * @returns Payload formatted for the backend API
 *
 * @remarks
 * - Arc Mosaico projects are always created as templates
 * - Cover image is only included if it's a valid base64 string
 * - Grades are sent as array of grade IDs from /api/arc_mosaico/grades
 */
export const transformToApiPayload = (formData: ProjectFormData): ProjectPayload => {
  const objectivesHtml =
    formData.learningGoals.length > 0
      ? `<ul>${formData.learningGoals.map(goal => `<li>${goal}</li>`).join('')}</ul>`
      : ''

  const stagesText = formData.topics.length > 0 ? formData.topics.join(', ') : ''

  const coverImage = formData.coverImageUrl && isBase64(formData.coverImageUrl) ? formData.coverImageUrl : null

  const payload: ProjectPayload = {
    name: formData.projectName || 'Novo Projeto',
    cover_image: coverImage,
    kind: 'template',
    purpose: formData.drivingQuestion || '',
    modality: 'project',
    objectives: objectivesHtml,
    stages: stagesText,
    final_product_kind: '',
    final_product_description: formData.deliverable || '',
    grades: formData.gradeLevel ? [formData.gradeLevel] : [],
    share_community: false,
    synopsis: formData.summary || '',
    evaluation_tools_attributes: [],
  }

  if (!coverImage) {
    delete payload.cover_image
  }

  return payload
}

/**
 * Transforms form data to API payload format for project updates
 *
 * @param formData - Form data from ArcNewProject
 * @returns Payload formatted for the backend API (excludes grades and kind)
 *
 * @remarks
 * Does NOT send grades or kind to avoid curriculum validation on backend
 * (Arc Mosaico schools don't have courses configured)
 */
export const transformToUpdatePayload = (formData: ProjectFormData): ProjectUpdatePayload => {
  const objectivesHtml =
    formData.learningGoals.length > 0
      ? `<ul>${formData.learningGoals.map(goal => `<li>${goal}</li>`).join('')}</ul>`
      : ''

  const stagesText = formData.topics.length > 0 ? formData.topics.join(', ') : ''

  const coverImage = formData.coverImageUrl && isBase64(formData.coverImageUrl) ? formData.coverImageUrl : null

  const payload: ProjectUpdatePayload = {
    name: formData.projectName || 'Novo Projeto',
    purpose: formData.drivingQuestion || '',
    modality: 'project',
    objectives: objectivesHtml,
    stages: stagesText,
    final_product_kind: '',
    final_product_description: formData.deliverable || '',
    share_community: false,
    synopsis: formData.summary || '',
    evaluation_tools_attributes: [],
  }

  if (coverImage) {
    payload.cover_image = coverImage
  }

  return payload
}

/** Result of payload validation */
export interface ValidationResult {
  valid: boolean
  errors: string[]
}

/**
 * Validates if the form data is ready for submission
 *
 * @param formData - Form data to validate
 * @returns Validation result with errors if any
 *
 * @remarks
 * Minimum requirement: project name with at least 3 characters
 * Grades can be empty for draft projects
 */
export const validatePayload = (formData: ProjectFormData): ValidationResult => {
  const errors: string[] = []

  if (!formData.projectName || formData.projectName.trim().length < 3) {
    errors.push('Nome do projeto deve ter pelo menos 3 caracteres')
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * Parses HTML list, JSON array, or JSON string to array of strings
 *
 * @param data - Data to parse (can be HTML string, JSON string, or array)
 * @returns Array of strings extracted from the data
 *
 * @remarks
 * API may return objectives in different formats:
 * - String JSON: "[\"goal 1\", \"goal 2\"]"
 * - Array: ["goal 1", "goal 2"]
 * - HTML: <ul><li>goal 1</li><li>goal 2</li></ul>
 */
const parseHtmlListToArray = (data: string | string[] | unknown): string[] => {
  if (!data) return []

  if (Array.isArray(data)) {
    return data.filter(item => item && String(item).trim().length > 0)
  }

  const str = String(data).trim()

  if (str.startsWith('[')) {
    try {
      const parsed = JSON.parse(str)
      if (Array.isArray(parsed)) {
        return parsed.filter(item => item && String(item).trim().length > 0)
      }
    } catch {
      // Continue to HTML parsing if JSON parse fails
    }
  }

  const liRegex = /<li[^>]*>(.*?)<\/li>/gi
  const matches = str.match(liRegex)

  if (!matches) return []

  return matches
    .map((li: string) => {
      return li
        .replace(/<li[^>]*>/gi, '')
        .replace(/<\/li>/gi, '')
        .replace(/<[^>]+>/g, '')
        .trim()
    })
    .filter((item: string) => item.length > 0)
}

/**
 * Transforms API response to ArcNewProject form data format
 *
 * @param apiResponse - Response from the project API
 * @returns Partial form data for populating the form in edit mode
 *
 * @remarks
 * Handles both camelCase and snake_case field names from API
 * Cover image is set to null as it's loaded separately via loadCoverImage()
 */
export const transformApiResponseToFormData = (apiResponse: ProjectApiResponse): Partial<ProjectFormData> => {
  const objectivesRaw = apiResponse.objectives || ''
  const learningGoals = parseHtmlListToArray(objectivesRaw)

  const stagesRaw = apiResponse.stages || ''
  let topics: string[] = []

  if (stagesRaw) {
    if (typeof stagesRaw === 'string' && stagesRaw.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(stagesRaw)
        if (Array.isArray(parsed)) {
          topics = parsed.filter(s => s && String(s).trim().length > 0)
        }
      } catch {
        topics = stagesRaw
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0)
      }
    } else {
      topics = stagesRaw
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0)
    }
  }

  const skillsAndStandards: SelectedSkillStandard[] = transformApiProjectItemsToFormState(apiResponse.project_items)

  const deliverable = apiResponse.finalProductDescription || apiResponse.final_product_description || ''

  const coverImageUrl = null

  const schoolTermId = apiResponse.schoolTermId || apiResponse.school_term_id || null
  const groupSchoolTermId = apiResponse.groupSchoolTermId || apiResponse.group_school_term_id || null

  const drivingQuestion = apiResponse.purpose || apiResponse.guidingQuestion || apiResponse.guiding_question || ''

  const grades = apiResponse.grades
  let gradeLevel: string | null = null
  if (Array.isArray(grades) && grades.length > 0) {
    const firstGrade = grades[0]
    gradeLevel = typeof firstGrade === 'object' && firstGrade?.id ? String(firstGrade.id) : String(firstGrade)
  }

  return {
    description: apiResponse.synopsis || '',
    topics,
    gradeLevel,
    schoolTermId,
    groupSchoolTermId,
    projectName: apiResponse.name || '',
    drivingQuestion,
    summary: apiResponse.synopsis || '',
    learningGoals,
    deliverable,
    coverImageUrl,
    skillsAndStandards,
  }
}
